// Shared read-focused table primitive (FRONTEND-BLUEPRINT round-1 contract). Every persona LIST screen
// composes this so tables are consistent across the rebuild. Generic over the row type; columns declare a
// header + an accessor/render. Handles loading (skeleton), empty state, and an optional row click.
import type { ReactNode } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export interface Column<Row> {
  key: string;
  header: ReactNode;
  /** cell content; defaults to String(row[key]). */ render?: (row: Row) => ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
}

export function DataTable<Row extends Record<string, unknown>>({
  columns, rows, loading, empty, getRowId, onRowClick, rowActions,
}: {
  columns: Column<Row>[];
  rows: Row[];
  loading?: boolean;
  empty?: ReactNode;
  getRowId: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  /** optional trailing actions column. */ rowActions?: (row: Row) => ReactNode;
}) {
  const align = (a?: string) => (a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left");
  if (loading) {
    return (
      <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
    );
  }
  if (rows.length === 0) {
    return <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">{empty ?? "Geen gegevens."}</div>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((c) => <TableHead key={c.key} className={cn(align(c.align), c.className)}>{c.header}</TableHead>)}
            {rowActions && <TableHead className="text-right">·</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={getRowId(row)} className={onRowClick ? "cursor-pointer" : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}>
              {columns.map((c) => (
                <TableCell key={c.key} className={cn(align(c.align), c.className)}>
                  {c.render ? c.render(row) : String(row[c.key] ?? "—")}
                </TableCell>
              ))}
              {rowActions && <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>{rowActions(row)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
